/**
 * RFC 9457 problem details, plus the domain code.
 *
 * `ARCHITECTURE.md` §API conventions: "Errors are RFC 9457 problem details:
 * `{ type, title, status, detail, code, errors? }`… Clients see codes, never stack
 * traces." The `code` enum is built from `errors.ts`, so a code the table does not
 * know cannot be sent.
 */
import { z } from 'zod';
import { DOMAIN_ERROR_CODES } from '../errors.js';
export const DomainErrorCodeSchema = z
    .enum(DOMAIN_ERROR_CODES)
    .describe('The domain error code. Branch on this, not on the status or the message — the set is ' +
    'closed and every code means one thing (ARCHITECTURE.md, Guardrails).');
export const ProblemFieldErrorSchema = z
    .object({
    path: z
        .string()
        .describe('The field that failed, dotted from the root of the body — "items.0.blocks".'),
    message: z.string().describe('What was wrong with it, in one sentence.'),
    code: z
        .string()
        .optional()
        .describe('A stable code for what was wrong, on an Unprocessable refusal — ' +
        '"unfinished_marker", "invalid_attribute". Branch on it rather than on the message.'),
    field: z
        .string()
        .optional()
        .describe('On the public form door: the field the error is about, by its name — or the ' +
        'header, "Idempotency-Key". The same as path there.'),
})
    .describe('One field-level validation failure.');
export const ProblemSchema = z
    .object({
    type: z
        .string()
        .describe('A URI identifying the problem type. Stable per code; safe to link to.'),
    title: z
        .string()
        .describe('A short summary of the problem type, the same for every occurrence.'),
    status: z.int().min(100).max(599).describe('The HTTP status code, repeated in the body.'),
    detail: z.string().describe('What went wrong this time, specific to this occurrence.'),
    code: DomainErrorCodeSchema,
    instance: z
        .string()
        .optional()
        .describe('A URI for this occurrence — usually the request id, for support.'),
    errors: z
        .array(ProblemFieldErrorSchema)
        .optional()
        .describe('Present when validation failed: one entry per field.'),
    meta: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
        .optional()
        .describe('The small facts this occurrence carries, for branching rather than reading: ' +
        'retry_after on RateLimited, the seat limit on SeatLimit, the missing scope on ' +
        'Forbidden. Flat, never a secret, never a whole row (ARCHITECTURE.md, Guardrails: ' +
        '"an agent can branch instead of guess").'),
})
    .describe('An error response. Every non-2xx body in the API is one of these.');
//# sourceMappingURL=problem.js.map